import React, { useCallback, useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { useDispatch, useSelector } from "react-redux";
import { logOut } from "../auth/authActions";
import { useNavigate, useLocation } from "react-router-dom";
import { clone, partial, pluck } from "ramda";
import { notBlank } from "../util/ext";
import { useSnackAlert } from "../common/use-snackAlert";
import { useSearchFilter } from "../common/use-searchFilter";
import {
  useAllModules,
  useCurationCreate,
  useSubscriptionDelete,
} from "./subscriptionService";
import { useOrganizations } from "../user-admin/userServices";

import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import Switch from "@mui/material/Switch";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";

import FilterBar from "../creator-application/common/FilterBar";
import ModuleAutoComplete from "./ModuleAutoComplete";
import OrganizationAutoComplete from "../user-admin/OrganizationAutoComplete";
import PanelLoadingIndicator from "../components/PanelLoadingIndicator";
import SubscriptionDialog from "./SubscriptionDialog";
import SubscriptionTable from "./SubscriptionTable";
import SubscriptionTypeSelect from "./SubscriptionTypeSelect";
import TotalsHeader from "../creator-application/common/TotalsHeader";

const useStyles = scrollVisible =>
  makeStyles(theme => ({
    root: {
      border: "3px white solid",
      backgroundColor: "white",
      borderRadius: "20px",
    },

    panel: {
      backgroundColor: "white",
      paddingLeft: theme.spacing(4),
      paddingRight: scrollVisible ? theme.spacing(0) : theme.spacing(4),
    },

    listContainer: {
      height: "calc(100vh - 350px)",
      overflow: "auto",
    },
  }));

const SubscriptionsBrowse = ({
  searchState,
  onSearchStateChange,
  searchResults,
}) => {
  const [scrollVisible, setScrollVisible] = useState(true);
  const auth = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const classes = useStyles(scrollVisible)();
  const navigate = useNavigate();
  const location = useLocation();
  const { SnackAlert, showAlert } = useSnackAlert();
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);

  const [modules, setModules] = useState([]);
  const allModules = useAllModules();
  useEffect(() => {
    if (allModules.isSuccess && notBlank(allModules.data)) {
      setModules(allModules.data);
    }
  }, [allModules.isSuccess, allModules.data]);

  const [organizations, setOrganizations] = useState([]);
  const allOrganizations = useOrganizations();
  useEffect(() => {
    if (allOrganizations.isSuccess && notBlank(allOrganizations.data)) {
      setOrganizations(allOrganizations.data);
    }
  }, [allOrganizations.isSuccess, allOrganizations.data]);

  // //-- Handle Search state
  const initialState = clone(searchState);
  const {
    state,
    handleUpdateSort,
    handleUpdatePage,
    handleUpdateModule,
    handleUpdateOrganization,
    handleUpdateTypes,
    handleUpdateActiveOnly,
    handleUpdatePerpetualOnly,
    handleUpdateIncludeIndirect,
    handleUpdateSearch,
  } = useSearchFilter(initialState);

  //-- Whenever the internal search state changes, update the search state stored in the parent
  useEffect(() => {
    onSearchStateChange(state);
    setScrollVisible(true);
  }, [onSearchStateChange, state]);

  useEffect(() => {
    if (searchResults.isError && searchResults.error.status === 401) {
      dispatch(logOut());
    }

    if (searchResults.isSuccess) {
      setScrollVisible(true);
      setSubscriptions(searchResults.data);
    }
  }, [
    dispatch,
    auth,
    searchResults.data,
    searchResults.isError,
    searchResults.error,
    searchResults.isSuccess,
  ]);

  const handleScrollVisible = useCallback(visible => {
    setScrollVisible(visible);
  }, []);

  const subscriptionDelete = useSubscriptionDelete();
  const handleSubscriptionDeleteClick = useCallback(
    async subscription => {
      await subscriptionDelete.mutateAsync({
        resourceIds: { id: subscription.id },
      });
      await searchResults.refetch();
    },
    [subscriptionDelete, searchResults],
  );

  const curationCreate = useCurationCreate();
  const handleCreateCurationClick = useCallback(
    async subscription => {
      try {
        await curationCreate.mutateAsync({
          data: {
            organizationId: subscription.organizationId,
            moduleId: subscription.mediaId,
          },
        });
        await searchResults.refetch();
        showAlert("success", [`Curation Created.`]);
      } catch (err) {
        if (notBlank(err.body)) {
          showAlert("error", pluck("message", err.body.errors));
        } else {
          showAlert("error", [err.message]);
        }
      }
    },
    [curationCreate, searchResults, showAlert],
  );

  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const handleSubscriptionEditClick = useCallback(subscription => {
    setCurrentSubscription(subscription);
    setSubscriptionDialogOpen(true);
  }, []);

  const handleNewSubscriptionClicked = useCallback(() => {
    setSubscriptionDialogOpen(true);
  }, []);

  const handleSubscriptionDialogClose = useCallback(() => {
    setSubscriptionDialogOpen(false);
    setCurrentSubscription(null);
  }, []);

  const handleSubscriptionDialogSave = useCallback(
    async subs => {
      setSubscriptionDialogOpen(false);
      setCurrentSubscription(null);
      showAlert("success", [
        `${subs.length} Subscription${subs.length !== 1 ? "s" : ""} saved.`,
      ]);
      await searchResults.refetch();
    },
    [showAlert, searchResults],
  );

  return (
    <Container id="subscription-browse-view" classes={{ root: classes.root }}>
      <SnackAlert />

      {subscriptionDialogOpen && (
        <SubscriptionDialog
          open={subscriptionDialogOpen}
          subscription={currentSubscription}
          organizationId={state.appliedOrganization}
          organizationList={organizations}
          moduleList={modules}
          onClose={handleSubscriptionDialogClose}
          onSave={handleSubscriptionDialogSave}
        />
      )}

      <div>
        <FilterBar
          backButton={
            notBlank(location.state) &&
            notBlank(location.state.previousLocation) && (
              <Button
                aria-label="Go back"
                onClick={() => navigate(location.state.previousLocation)}
                startIcon={<FontAwesomeIcon icon={faArrowLeft} />}
              >
                Back
              </Button>
            )
          }
          otherFilters={
            <Grid
              container
              alignItems="center"
              direction="row"
              spacing={2}
              classes={{ root: classes.filters }}
            >
              <Grid item xs={12} sm={6} md={4}>
                <OrganizationAutoComplete
                  value={state.appliedOrganization}
                  label="Filter By Organization"
                  onChange={handleUpdateOrganization}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <ModuleAutoComplete
                  value={state.appliedModule}
                  label="Filter By Module"
                  onChange={handleUpdateModule}
                />
              </Grid>

              <Grid item xs={3} md={2}>
                <SubscriptionTypeSelect
                  value={state.appliedTypes}
                  label="Filter By Type"
                  onChange={handleUpdateTypes}
                />
              </Grid>

              <Grid item xs={3} md={2}>
                <LocalizationProvider dateAdapter={AdapterLuxon}>
                  <DatePicker
                    format="MM/dd/yyyy"
                    label="Expires Before"
                    value={searchState.expiresBefore}
                    onChange={partial(handleUpdateSearch, ["expiresBefore"])}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        id: "expiresBefore",
                      },
                      openPickerButton: {
                        "aria-label": "change expires before",
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              {notBlank(state.appliedOrganization) && (
                <Grid item>
                  <Box display="flex" justifyContent="center">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={state.includeIndirect}
                          onChange={event =>
                            handleUpdateIncludeIndirect(event.target.checked)
                          }
                          name="includeIndirect"
                          color="primary"
                        />
                      }
                      label="Include Indirect"
                    />
                  </Box>
                </Grid>
              )}
              <Grid item xs="auto">
                <Box display="flex" justifyContent="center">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={state.activeOnly}
                        onChange={event =>
                          handleUpdateActiveOnly(event.target.checked)
                        }
                        name="activeOnly"
                        color="primary"
                      />
                    }
                    label="Active Only"
                  />
                </Box>
              </Grid>

              <Grid item xs="auto">
                <Box display="flex" justifyContent="center">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={state.perpetualOnly}
                        onChange={event =>
                          handleUpdatePerpetualOnly(event.target.checked)
                        }
                        name="perpetualOnly"
                        color="primary"
                      />
                    }
                    label="Perpetual Only"
                  />
                </Box>
              </Grid>
            </Grid>
          }
        />

        <TotalsHeader
          countText="Subscription"
          count={searchResults.isSuccess ? subscriptions.length : 0}
          showCount={false}
          selectedActions={
            <Box>
              <Button
                variant={"text"}
                startIcon={<AddIcon />}
                onClick={handleNewSubscriptionClicked}
              >
                New Subscription
              </Button>
            </Box>
          }
        />

        {searchResults.isLoading && <PanelLoadingIndicator />}
        {searchResults.isSuccess && (
          <Container classes={{ root: classes.panel }}>
            {notBlank(subscriptions) ? (
              <SubscriptionTable
                subscriptionList={subscriptions}
                organizationList={organizations}
                organizationId={state.appliedOrganization}
                page={state.page}
                orderBy={state.orderBy}
                order={state.orderDirection}
                onSortChange={handleUpdateSort}
                onPageChange={handleUpdatePage}
                onDeleteClicked={handleSubscriptionDeleteClick}
                onEditClicked={handleSubscriptionEditClick}
                onCreateCurationClick={handleCreateCurationClick}
                onScrollVisible={handleScrollVisible}
              />
            ) : (
              <Box
                height="calc(100vh - 400px)"
                padding={5}
                fontSize="1.6rem"
                fontStyle="italic"
              >
                No Subscriptions found matching given criteria.
              </Box>
            )}
          </Container>
        )}
      </div>
    </Container>
  );
};

export default SubscriptionsBrowse;
